import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { type Database, DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import {
  getReferencedJiraIssueRefs,
  isJiraIssueDetailsResolved,
} from '@databases/utils/database/field-types/jiraIssueDetails';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';

import useView from './useView';

function extractJiraFilterFieldIds(filter: DatabaseFilter, database: Database): string[] {
  const jiraFieldIds: string[] = [];

  const processFilter = (f: DatabaseFilter) => {
    if (isFilterWithUsageType(f)) {
      for (const subFilter of f.value) {
        processFilter(subFilter);
      }
    } else {
      const field = getDatabaseFieldOptional(database, f.fieldId);
      if (field?.type === DatabaseFieldType.JIRA_ISSUE_DETAILS) {
        jiraFieldIds.push(f.fieldId);
      }
    }
  };

  processFilter(filter);
  return jiraFieldIds;
}

export function hasUnresolvedJiraEntries(
  database: Database,
  jiraFieldIds: string[],
  state: DefaultRootState
): boolean {
  for (const entryId of database.entries) {
    const entry = getDatabaseEntryOptional(database, entryId);
    if (!entry) {
      continue;
    }

    for (const fieldId of jiraFieldIds) {
      const field = getDatabaseFieldOptional(database, fieldId);
      if (!field) {
        continue;
      }

      const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);
      if (issueRefs.length === 0) {
        continue;
      }

      const isResolved = isJiraIssueDetailsResolved({ database, field, entry }, state);
      if (!isResolved) {
        return true;
      }
    }
  }

  return false;
}

export function useJiraFilterLoading(contentId: string): boolean {
  const { view } = useView();
  const filter = view.settings.filter;

  const isLoading = defaultStoreUtils.useEqualSelector((state) => {
    if (!filter) {
      return false;
    }

    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const jiraFieldIds = extractJiraFilterFieldIds(filter, database);

    if (jiraFieldIds.length === 0) {
      return false;
    }

    return hasUnresolvedJiraEntries(database, jiraFieldIds, state);
  });

  return isLoading;
}
